import { useCallback, useEffect, useMemo, useState } from "react";
import CustomLoader from "../../../../components/CustomLoader";
import CollectionTabs from "../CollectionTabs";
import Pagination from "../Pagination";
import Table from "../../../../components/Table";
import toDisplayCase from "../../../../toDisplayCase";

const ROWS_PER_PAGE = 15;
const HIDDEN_FIELD_TYPES = new Set(["Relation", "Array", "Object", "File"]);

function getExcludeFields(schema) {
  if (!schema?.fields) return ["acl", "id", "created", "updated"];
  return Object.keys(schema.fields).reduce(
    (acc, key) => {
      const options = schema.fields[key];
      if (options.read === false || options._read === false) {
        acc.push(key);
        return acc;
      }
      const type = options._type ?? options.type;
      if (HIDDEN_FIELD_TYPES.has(type)) acc.push(key);
      return acc;
    },
    ["acl", "id", "created", "updated"],
  );
}

function ContainerTable({
  schema,
  onCollapse,
  onSort,
  onExport,
  selected,
  objects = [],
  progress,
  count,
  collectionName,
  purchasesSchema,
  salesSchema,
  onChangeTab,
  ...props
}) {
  const [currentPage, setCurrentPage] = useState(1);

  const displayCollectionName = useMemo(
    () => toDisplayCase(collectionName ?? ""),
    [collectionName],
  );

  const handleTabChange = useCallback(
    (selectedTabName) => {
      const next = selectedTabName?.toLowerCase();
      if (next === collectionName) return;
      onChangeTab?.(next);
    },
    [collectionName, onChangeTab],
  );

  const isPurchases = collectionName === "purchases";
  const fields = useMemo(
    () => (isPurchases ? purchasesSchema?.fields : salesSchema?.fields) ?? {},
    [isPurchases, purchasesSchema, salesSchema],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [collectionName]);

  const excludeFields = useMemo(() => getExcludeFields(schema), [schema]);

  const { currentRows, totalPages, indexOfFirstRow, start, end, totalObjects } =
    useMemo(() => {
      const total = objects.length;
      const indexOfLastRow = currentPage * ROWS_PER_PAGE;
      const indexOfFirst = indexOfLastRow - ROWS_PER_PAGE;
      const rows = objects.slice(indexOfFirst, indexOfLastRow);
      return {
        currentRows: rows,
        totalPages: Math.ceil(total / ROWS_PER_PAGE),
        indexOfFirstRow: indexOfFirst,
        start: indexOfFirst + 1,
        end: indexOfFirst + rows.length,
        totalObjects: total,
      };
    }, [objects, currentPage]);

  const handleExport = useCallback(
    (e) => {
      e.preventDefault();
      onExport(collectionName, objects);
    },
    [collectionName, objects, onExport],
  );

  const showCount = objects && selected != null && count != null;

  return (
    <div
      className="bg-white rounded-3 shadow-sm overflow-hidden"
      style={{ border: "1px solid #e9ecef" }}
    >
      <div className="px-3 pt-3" style={{ borderBottom: "1px solid #e9ecef" }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          {showCount && (
            <div className="d-flex align-items-center gap-2">
              <span
                className="badge rounded-pill"
                style={{
                  backgroundColor: "#eef2ff",
                  color: "#4b5c7c",
                  fontSize: "12px",
                  fontWeight: 600,
                }}
              >
                <i className="bi bi-list-ul me-1" />
                {selected.length > 0
                  ? `Selected: ${selected.length}`
                  : `${start}–${end} of ${totalObjects}`}
              </span>
            </div>
          )}

          <button
            type="button"
            className="btn btn-sm bg-success fw-medium d-flex align-items-center gap-2 ms-auto"
            style={{
              color: "white",
              borderRadius: "8px",
              padding: "6px 14px",
              boxShadow: "0 2px 6px rgba(62, 179, 66, 0.3)",
              opacity: objects.length === 0 ? 0.5 : 1,
            }}
            disabled={objects.length === 0}
            onClick={handleExport}
          >
            <i className="bi bi-file-earmark-arrow-down" />
            Export Reports
          </button>
        </div>

        <CollectionTabs
          collectionName={displayCollectionName}
          onChangeTab={handleTabChange}
          {...props}
        />
      </div>

      {/* Body */}
      <div className="p-3">
        {progress ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "200px" }}
          >
            <CustomLoader />
          </div>
        ) : objects.length === 0 ? (
          <div
            className="d-flex flex-column justify-content-center align-items-center text-muted gap-2"
            style={{ minHeight: "200px" }}
          >
            <i
              className="bi bi-inbox"
              style={{ fontSize: "2rem", opacity: 0.4 }}
            />
            <span style={{ fontSize: "14px" }}>No records found</span>
          </div>
        ) : (
          <Table
            fields={fields}
            numbering={indexOfFirstRow}
            onCollapse={onCollapse}
            collapsable
            objects={currentRows}
            excludeFields={excludeFields}
            selected={selected}
            progress={progress}
            onSort={onSort}
            showActions={false}
            className="mt-1"
          />
        )}
      </div>

      {totalPages > 1 && (
        <div className="px-3 pb-3" style={{ borderTop: "1px solid #e9ecef" }}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            setCurrentPage={setCurrentPage}
            {...props}
          />
        </div>
      )}
    </div>
  );
}

export default ContainerTable;
